import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  IconButton, 
  Box,
  Typography,
  Tooltip,
  alpha,
  Paper,
  Avatar
} from '@mui/material';
import { 
  People as PeopleIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  DirectionsCar as DirectionsCarIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';

const Sidebar = ({ open, toggleDrawer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // SafeLine color scheme
  const colors = {
    primary: {
      flame: '#ff6b35',
      bright: '#ff9500', 
      deep: '#e55100',
      tint: '#fff4f0'
    },
    base: {
      white: '#ffffff',
      lightGray: '#f8f9fa',
      coolGray: '#e9ecef',
      mediumGray: '#dee2e6'
    },
    text: {
      darkCharcoal: '#1f2937',
      mediumCharcoal: '#374151',
      lightCharcoal: '#6b7280',
      mutedGray: '#9ca3af'
    },
    success: {
      emerald: '#10b981',
      light: '#d1fae5'
    }
  };
  
  // For demo purposes, we'll show all menu items regardless of permissions
  const demoHasPermission = (permission) => true;
  
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      permission: 'view_dashboard'
    },
    {
      text: 'Pickup Logs',
      icon: <DirectionsCarIcon />,
      path: '/pickup-logs',
      permission: 'view_pickup_logs'
    },
    {
      text: 'Teachers',
      icon: <PeopleIcon />,
      path: '/teachers',
      permission: 'view_teachers'
    },
    {
      text: 'Parents',
      icon: <PeopleIcon />,
      path: '/parents',
      permission: 'view_guardians'
    },
    {
      text: 'Students',
      icon: <SchoolIcon />,
      path: '/students',
      permission: 'view_students'
    },
    {
      text: 'Guests',
      icon: <PersonIcon />,
      path: '/guests',
      permission: 'view_guardians'
    },
    {
      text: 'Tagging Identifier',
      icon: <LocalOfferIcon />,
      path: '/tagging-identifier',
      permission: 'view_dashboard'
    }
  ];

  // Helper to check if a menu item is active (for nested routes)
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? 240 : 70,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 70,
          boxSizing: 'border-box',
          backgroundColor: colors.base.white,
          color: colors.text.darkCharcoal,
          transition: 'width 0.3s ease',
          borderRight: `1px solid ${colors.base.coolGray}`,
          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      {/* Main content container with flexbox */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        position: 'relative'
      }}>
        {/* Scrollable content area */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header with SafeLine branding */}
          <Paper 
            elevation={0}
            sx={{ 
              backgroundColor: colors.primary.flame,
              backgroundImage: `linear-gradient(135deg, ${colors.primary.flame} 0%, ${colors.primary.deep} 100%)`,
              borderRadius: 0,
              mb: 0
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: open ? 'space-between' : 'center',
                padding: 2,
                minHeight: 64
              }}
            >
              {open ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ 
                      mr: 1.5, 
                      fontSize: 28, 
                      color: colors.base.white,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }} />
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: colors.base.white,
                        letterSpacing: '0.5px',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      SafeLine
                    </Typography>
                  </Box>
                  <IconButton 
                    onClick={toggleDrawer} 
                    sx={{ 
                      color: colors.base.white,
                      backgroundColor: alpha(colors.base.white, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(colors.base.white, 0.2),
                      }
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </>
              ) : (
                <Tooltip title="Expand Menu" placement="right">
                  <IconButton 
                    onClick={toggleDrawer} 
                    sx={{ 
                      color: colors.base.white,
                      backgroundColor: alpha(colors.base.white, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(colors.base.white, 0.2),
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Paper>
          
          {/* User profile section */}
          {open && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              p: 2,
              mb: 2,
              mx: 2,
              mt: 2,
              borderRadius: 2,
              backgroundColor: colors.base.lightGray,
              border: `1px solid ${colors.base.coolGray}`,
            }}>
              <Avatar 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  mb: 1,
                  bgcolor: colors.primary.flame,
                  border: `3px solid ${colors.base.white}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                A
              </Avatar>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                color: colors.text.darkCharcoal,
                fontSize: '0.95rem'
              }}>
                Admin User
              </Typography>
              <Typography variant="body2" sx={{ 
                color: colors.text.lightCharcoal, 
                fontSize: '0.8rem' 
              }}>
                School Administrator
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                mt: 1.5, 
                pt: 1.5, 
                width: '100%', 
                justifyContent: 'center',
                borderTop: `1px solid ${colors.base.coolGray}`,
                gap: 1
              }}>
                <Tooltip title="Settings" placement={open ? undefined : "right"}>
                  <IconButton 
                    size="medium" 
                    sx={{ 
                      color: colors.text.mediumCharcoal,
                      mb: !open ? 0.5 : undefined,
                      '&:hover': {
                        color: colors.primary.flame,
                        backgroundColor: colors.primary.tint
                      }
                    }}
                    onClick={() => navigate('/settings')}
                  >
                    <SettingsIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Help" placement={open ? undefined : "right"}>
                  <IconButton 
                    size="medium" 
                    sx={{ 
                      color: colors.text.mediumCharcoal,
                      '&:hover': {
                        color: colors.primary.flame,
                        backgroundColor: colors.primary.tint
                      }
                    }}
                    onClick={() => navigate('/help')}
                  >
                    <HelpIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="User Settings" placement={open ? undefined : "right"}>
                  <IconButton 
                    size="medium" 
                    sx={{ 
                      color: colors.text.mediumCharcoal,
                      '&:hover': {
                        color: colors.primary.flame,
                        backgroundColor: colors.primary.tint
                      }
                    }}
                    onClick={() => navigate('/settings/user')}
                  >
                    <InfoIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
          
          <Divider sx={{ 
            my: 1, 
            mx: 2,
            borderColor: colors.base.coolGray,
            display: open ? 'block' : 'none'
          }} />
          
          <Typography 
            variant="overline" 
            sx={{ 
              px: 3, 
              color: colors.text.darkCharcoal,
              fontWeight: 'bold',
              display: open ? 'block' : 'none',
              fontSize: '0.7rem',
              letterSpacing: '1px'
            }}
          >
            MAIN NAVIGATION
          </Typography>
          
          <List sx={{ px: 1, mt: 1, flex: 1 }}>
            {menuItems.map((item) => (
              // Using our demo permission check to show all items
              demoHasPermission(item.permission) && (
                <Tooltip 
                  key={item.text}
                  title={open ? "" : item.text} 
                  placement="right"
                  disableHoverListener={open}
                >
                  <ListItem
                    button
                    component={Link}
                    to={item.path}
                    selected={isActive(item.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      borderRadius: 1.5,
                      mb: 0.8,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      '&::before': isActive(item.path) ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        height: '60%',
                        width: '4px',
                        backgroundColor: colors.primary.flame,
                        borderRadius: '0 4px 4px 0'
                      } : {},
                      '&.Mui-selected': {
                        backgroundColor: colors.primary.tint,
                        '&:hover': {
                          backgroundColor: alpha(colors.primary.flame, 0.1),
                        }
                      },
                      '&:hover': {
                        backgroundColor: colors.base.lightGray,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      },
                      '& .MuiListItemIcon-root': {
                        transition: 'transform 0.2s ease',
                      },
                      '&:hover .MuiListItemIcon-root': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: isActive(item.path) 
                          ? colors.primary.flame 
                          : colors.text.darkCharcoal
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      sx={{ 
                        opacity: open ? 1 : 0,
                        display: open ? 'block' : 'none',
                        '& .MuiTypography-root': {
                          fontWeight: isActive(item.path) ? '600' : '500',
                          color: `${colors.text.darkCharcoal} !important`,
                          fontSize: '0.9rem'
                        }
                      }} 
                    />
                  </ListItem>
                </Tooltip>
              )
            ))}
          </List>
          
          {open && (
            <>
              <Divider sx={{ 
                my: 2, 
                mx: 2,
                borderColor: colors.base.coolGray
              }} />
              
              <Typography 
                variant="overline" 
                sx={{ 
                  px: 3, 
                  color: colors.text.darkCharcoal,
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  letterSpacing: '1px'
                }}
              >
                SYSTEM
              </Typography>
              
              <List sx={{ px: 1, mt: 1 }}>
                <ListItem
                  button
                  component={Link}
                  to="/settings"
                  selected={isActive('/settings')}
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                    borderRadius: 1.5,
                    mb: 0.8,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': isActive('/settings') ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      height: '60%',
                      width: '4px',
                      backgroundColor: colors.primary.flame,
                      borderRadius: '0 4px 4px 0'
                    } : {},
                    '&:hover': {
                      backgroundColor: colors.base.lightGray,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 3,
                      justifyContent: 'center',
                      color: isActive('/settings') ? colors.primary.flame : colors.text.mediumCharcoal,
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Settings" 
                    sx={{ 
                      '& .MuiTypography-root': {
                        fontWeight: isActive('/settings') ? '600' : '500',
                        color: colors.text.darkCharcoal,
                        fontSize: '0.9rem'
                      }
                    }} 
                  />
                </ListItem>
                
                <ListItem
                  button
                  component={Link}
                  to="/help"
                  selected={location.pathname === '/help'}
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                    borderRadius: 1.5,
                    mb: 0.8,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': location.pathname === '/help' ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      height: '60%',
                      width: '4px',
                      backgroundColor: colors.primary.flame,
                      borderRadius: '0 4px 4px 0'
                    } : {},
                    '&:hover': {
                      backgroundColor: colors.base.lightGray,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 3,
                      justifyContent: 'center',
                      color: location.pathname === '/help' ? colors.primary.flame : colors.text.mediumCharcoal,
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <HelpIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Help & Support" 
                    sx={{ 
                      '& .MuiTypography-root': {
                        fontWeight: location.pathname === '/help' ? '600' : '500',
                        color: colors.text.darkCharcoal,
                        fontSize: '0.9rem'
                      }
                    }} 
                  />
                </ListItem>
              </List>
            </>
          )}
        </Box>
        
        {/* Footer - Always at bottom */}
        {!open && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 1,
          }}>
            <Tooltip title="Settings" placement={open ? undefined : "right"}>
              <IconButton 
                size="medium" 
                sx={{ 
                  color: colors.text.mediumCharcoal,
                  mb: !open ? 0.5 : undefined,
                  '&:hover': {
                    color: colors.primary.flame,
                    backgroundColor: colors.primary.tint
                  }
                }}
                onClick={() => navigate('/settings')}
              >
                <SettingsIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Help" placement={open ? undefined : "right"}>
              <IconButton 
                size="medium" 
                sx={{ 
                  color: colors.text.mediumCharcoal,
                  '&:hover': {
                    color: colors.primary.flame,
                    backgroundColor: colors.primary.tint
                  }
                }}
                onClick={() => navigate('/help')}
              >
                <HelpIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box sx={{ 
          p: 2,
          textAlign: 'center',
          borderTop: `1px solid ${colors.base.coolGray}`,
          backgroundColor: colors.base.lightGray
        }}>
          <Typography variant="caption" sx={{ 
            color: colors.text.mediumCharcoal, 
            fontSize: '0.7rem',
            fontWeight: '500'
          }}>
            SafeLine v1.2.0
          </Typography>
          <Typography variant="caption" sx={{ 
            display: 'block', 
            color: colors.text.mutedGray, 
            fontSize: '0.7rem',
            fontWeight: '400'
          }}>
            © 2025 SafeLine Security
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;